import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { createContext, useContext } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { useTheme } from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import { ToastMessage, ToastTitle } from './Toast.styles';

export interface ToastTypes {
  title?: string | undefined;
  message?: string | JSX.Element | undefined;
  type?: 'info' | 'success' | 'warning' | 'error';
  options?: ToastOptions | undefined;
  style?: React.CSSProperties | undefined;
}

interface IContext {
  openToast: (options?: ToastTypes) => void;
}

const defaultState = {
  openToast: () => null,
};

const ToastContext = createContext<IContext>(defaultState);

const defaultOptions: ToastOptions | undefined = {
  position: 'top-right',
  autoClose: 6000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
  progress: undefined,
  closeButton: () => <FontAwesomeIcon icon={faTimes} color="#fff" size="sm" />,
};

const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error(`useToast must be used within a ToastProvider`);
  }
  return context;
};

const ToastProvider = (props: any) => {
  const theme = useTheme();

  const openToast = ({ title, message, type, options, style }: ToastTypes) => {
    toast.dismiss();
    toast.configure();

    const markup = (
      <div data-testid="toast">
        {title && <ToastTitle data-testid="toast-title">{title}</ToastTitle>}
        {message && <ToastMessage data-testid="toast-message">{message}</ToastMessage>}
      </div>
    );

    const styles = {
      style: {
        background: theme.primary,
        color: '#fff',
        borderRadius: '6px',
        fontFamily: 'inherit',
        ...style,
      },
    };

    const opts = {
      ...defaultOptions,
      ...styles,
      ...options,
    };

    if (!type) toast(markup, opts);
    else toast[type](markup, opts);
  };

  const value = React.useMemo(() => {
    return {
      openToast,
    };
  }, []);

  return <ToastContext.Provider value={value} {...props} />;
};

export { ToastProvider, useToast };
